import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { intl } from '@trello/i18n';
import { showFlag } from '@trello/nachos/experimental-flags';

import { useUpdateWorkspaceMemberRoleMutation } from './UpdateWorkspaceMemberRoleMutation.generated';

export const useUpdateWorkspaceMemberRole = ({
  workspaceId,
  memberId,
}: {
  workspaceId: string;
  memberId: string;
}) => {
  const [updateWorkspaceMemberPermission, { loading }] =
    useUpdateWorkspaceMemberRoleMutation();

  const updateWorkspaceMemberRole = async ({
    memberType,
    onSuccess,
  }: {
    memberType: 'admin' | 'normal';
    onSuccess: () => void;
  }) => {
    const traceId = Analytics.startTask({
      taskName: 'edit-organization/members/role',
      source: 'workspaceMembersScreen',
    });

    return updateWorkspaceMemberPermission({
      variables: { memberId, workspaceId, type: memberType, traceId },
    })
      .then((response) => {
        if (response.errors) {
          throw new Error(response.errors[0].message);
        }
        Analytics.taskSucceeded({
          source: 'workspaceMembersScreen',
          traceId,
          taskName: 'edit-organization/members/role',
        });
        Analytics.sendTrackEvent({
          action: 'updated',
          actionSubject: 'role',
          source: 'workspaceMembersScreen',
          attributes: {
            trelloMemberId: memberId,
            value: memberType,
          },
          containers: formatContainers({ workspaceId }),
        });
        onSuccess?.();
      })
      .catch((error) => {
        Analytics.taskFailed({
          source: 'workspaceMembersScreen',
          traceId,
          error,
          taskName: 'edit-organization/members/role',
        });
        showFlag({
          appearance: 'error',
          title: intl.formatMessage({
            id: 'templates.workspace_member_roles.unknown-error',
            defaultMessage:
              'An unknown error occurred. Please try again later.',
            description:
              'Error message when updating a workspace member role fails',
          }),
          id: 'updateWorkspaceMemberRole',
          seed: memberId,
        });
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Workspace Member Role Popover',
          },
        });
      });
  };
  return { updateWorkspaceMemberRole, loading };
};
